import type { ReportsReturnDto } from '@terra/shared-contracts-reports';
import {
  REPORT_SOURCE_CHIP,
  type ReportSourceChip,
} from '../../../store/tab/reportsTabScopedFilterStore';

export type ReportsListFilterValues = {
  searchTerm: string;
  sourceChip: ReportSourceChip;
  createdStartDate: Date | null;
  createdEndDate: Date | null;
};

type ReportListItem = Pick<ReportsReturnDto, 'title' | 'source' | 'createdAt'>;

export const REPORT_SOURCE_CHIPS: readonly ReportSourceChip[] = [
  REPORT_SOURCE_CHIP.ALL,
  REPORT_SOURCE_CHIP.MANUAL,
  REPORT_SOURCE_CHIP.SCHEDULED,
  REPORT_SOURCE_CHIP.ON_DEMAND,
];

const toTime = (date: Date | string) => new Date(date).getTime();

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

const endOfDay = (date: Date) =>
  new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999,
  ).getTime();

const matchesSearch = ({
  report,
  searchTerm,
}: {
  report: ReportListItem;
  searchTerm: string;
}) => {
  const normalizedSearch = searchTerm.trim().toLowerCase();

  return (
    normalizedSearch === '' ||
    report.title.toLowerCase().includes(normalizedSearch)
  );
};

const matchesCreatedRange = ({
  report,
  createdStartDate,
  createdEndDate,
}: {
  report: ReportListItem;
  createdStartDate: Date | null;
  createdEndDate: Date | null;
}) => {
  const createdAt = toTime(report.createdAt);

  return (
    (createdStartDate === null || createdAt >= startOfDay(createdStartDate)) &&
    (createdEndDate === null || createdAt <= endOfDay(createdEndDate))
  );
};

export const filterReportsBeforeSource = <TReport extends ReportListItem>({
  reports,
  filters,
}: {
  reports: readonly TReport[];
  filters: ReportsListFilterValues;
}): TReport[] =>
  reports.filter(
    (report) =>
      matchesSearch({ report, searchTerm: filters.searchTerm }) &&
      matchesCreatedRange({
        report,
        createdStartDate: filters.createdStartDate,
        createdEndDate: filters.createdEndDate,
      }),
  );

export const filterReportsBySource = <TReport extends ReportListItem>({
  reports,
  sourceChip,
}: {
  reports: readonly TReport[];
  sourceChip: ReportSourceChip;
}): TReport[] =>
  sourceChip === REPORT_SOURCE_CHIP.ALL
    ? [...reports]
    : reports.filter((report) => report.source === sourceChip);

export const sortReportsNewestFirst = <TReport extends ReportListItem>(
  reports: readonly TReport[],
): TReport[] =>
  [...reports].sort(
    (left, right) => toTime(right.createdAt) - toTime(left.createdAt),
  );

export const countReportsBySourceChip = (
  reports: readonly ReportListItem[],
): Record<ReportSourceChip, number> =>
  reports.reduce<Record<ReportSourceChip, number>>(
    (counts, report) => ({
      ...counts,
      [report.source]: counts[report.source] + 1,
    }),
    {
      [REPORT_SOURCE_CHIP.ALL]: reports.length,
      [REPORT_SOURCE_CHIP.MANUAL]: 0,
      [REPORT_SOURCE_CHIP.SCHEDULED]: 0,
      [REPORT_SOURCE_CHIP.ON_DEMAND]: 0,
    },
  );

export const hasActiveReportListFilters = (
  filters: Pick<ReportsListFilterValues, 'createdStartDate' | 'createdEndDate'>,
) => filters.createdStartDate !== null || filters.createdEndDate !== null;

export const hasAnyReportFilters = (filters: ReportsListFilterValues) =>
  filters.searchTerm.trim() !== '' ||
  filters.sourceChip !== REPORT_SOURCE_CHIP.ALL ||
  hasActiveReportListFilters(filters);
